import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getStripeConnectStatus, connectStripeAccount } from '../services/stripeConnectService';
import { getSession } from '../lib/supabase';

const { FiCreditCard, FiRefreshCw } = FiIcons;

function maskAccountId(accountId) {
  if (!accountId || typeof accountId !== 'string') return '—';
  if (accountId.length <= 12) return '••••••••';
  return `${accountId.slice(0, 4)}••••${accountId.slice(-4)}`;
}

export default function StripeConnectCard({ role, userId }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const loadStatus = async () => {
    if (!userId || (role !== 'agent' && role !== 'admin')) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await getStripeConnectStatus({ role, userId });
    setStatus(error ? null : data);
    setLoading(false);
  };

  useEffect(() => {
    loadStatus();
  }, [role, userId]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const session = await getSession();
      const accessToken = session?.access_token ?? null;
      const { data, error } = await connectStripeAccount(accessToken);

      if (error) {
        toast.error(error.message || 'Could not start Stripe connection');
        setConnecting(false);
        return;
      }

      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast.error('No redirect URL received');
    } catch (err) {
      toast.error(err?.message || 'Something went wrong');
    } finally {
      setConnecting(false);
    }
  };

  if (role !== 'agent' && role !== 'admin') {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-slate-100 animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-32 bg-slate-100 rounded animate-pulse" />
            <div className="h-3 w-48 bg-slate-100 rounded animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  const connected = !!status?.stripe_connected_account_id;
  const onboardingComplete = !!status?.stripe_onboarding_completed;
  const chargesEnabled = !!status?.stripe_charges_enabled;
  const accountStatus = status?.stripe_account_status || null;

  let statusLabel = 'Not Connected';
  let statusClass = 'bg-slate-100 text-slate-700 border-slate-200';
  if (connected) {
    if (accountStatus === 'restricted' || !chargesEnabled) {
      statusLabel = 'Restricted / Action needed';
      statusClass = 'bg-amber-50 text-amber-700 border-amber-200';
    } else if (onboardingComplete && chargesEnabled) {
      statusLabel = 'Connected';
      statusClass = 'bg-emerald-50 text-emerald-700 border-emerald-200';
    } else {
      statusLabel = 'Pending';
      statusClass = 'bg-amber-50 text-amber-700 border-amber-200';
    }
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="p-6 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-slate-100 p-3">
              <SafeIcon icon={FiCreditCard} className="w-6 h-6 text-slate-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Stripe Connect</h2>
              <p className="text-sm text-slate-600">Receive payments for your events</p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusClass}`}>
            {statusLabel}
          </span>
        </div>
      </div>
      <div className="p-6 space-y-4">
        {connected ? (
          <>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Account ID</span>
              <span className="font-mono text-slate-800">{maskAccountId(status.stripe_connected_account_id)}</span>
            </div>
            {!chargesEnabled && (
              <p className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3">
                Complete onboarding in Stripe to accept payments. Click Reconnect to continue.
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleConnect}
                disabled={connecting}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 font-medium text-sm disabled:opacity-50"
              >
                {connecting ? (
                  <SafeIcon icon={FiRefreshCw} className="w-4 h-4 animate-spin" />
                ) : (
                  <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                )}
                Reconnect
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-slate-600">
              Connect your Stripe account to receive payments directly when students register for your paid events.
            </p>
            <button
              type="button"
              onClick={handleConnect}
              disabled={connecting}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-medium text-sm hover:shadow-lg disabled:opacity-50"
            >
              {connecting ? (
                <SafeIcon icon={FiRefreshCw} className="w-4 h-4 animate-spin" />
              ) : (
                <SafeIcon icon={FiCreditCard} className="w-4 h-4" />
              )}
              Connect Stripe Account
            </button>
          </>
        )}
      </div>
    </div>
  );
}
